import {UserModel} from 'app/modules/auth'
import clsx from 'clsx'
import {hasPermission, isAdmin} from 'common/HelperFunctions'
import {Select} from 'common/components/Fields'
import {USER_TYPES_NUMERIC} from 'common/constants'
import {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation} from 'react-router'
import {RootState, UserReducer} from 'setup'
import {getBranches} from 'setup/redux/Actions/General/getBranch'

const BRANCH_USER_TYPES = [
  USER_TYPES_NUMERIC.ADMIN,
  USER_TYPES_NUMERIC.STAFF,
  USER_TYPES_NUMERIC.TEACHER,
]

type Props = {
  className?: string
}

/**
 * "All Branches / <branch>" select. Persists the choice in localStorage and
 * reloads, since every list on the page is scoped by it. Mount it once per
 * viewport — it fetches the branch list on mount.
 */
const BranchSwitcher: FC<Props> = ({className}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const location = useLocation()

  const user = useSelector<RootState>(
    ({userReducer}) => (userReducer as UserReducer).user
  ) as UserModel

  const [branches, setBranches] = useState<any[]>([])
  const [loadingBranches, setLoadingBranches] = useState(false)
  const [branch, setBranch] = useState<string>(localStorage.getItem('branch') ?? '')

  const applies =
    BRANCH_USER_TYPES.includes(user.type as number) &&
    !location.pathname.includes('/institute/schedule/timetable')

  useEffect(() => {
    if (!applies) return
    let ignore = false
    setLoadingBranches(true)

    const onSuccess = (res: any) => {
      if (ignore) return
      const permitted = [{id: '', name: intl.formatMessage({id: 'GENERAL.ALL_BRANCHES'})}]
      for (const b of res.result ?? []) {
        if (isAdmin() || hasPermission(`branch_permission_${b.id}`)) permitted.push(b)
      }
      if (!localStorage.getItem('branch') && permitted.length > 0) {
        setBranch('')
        localStorage.removeItem('branch')
      }
      setBranches(permitted)
      setLoadingBranches(false)
    }

    const onFailure = () => {
      if (ignore) return
      setBranches([])
      setLoadingBranches(false)
    }

    dispatch(getBranches({}, onSuccess, onFailure))
    return () => {
      ignore = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applies])

  if (!applies) return null

  const handleBranch = (id: string) => {
    setBranch(id)
    if (id) {
      localStorage.setItem('branch', id)
    } else {
      localStorage.removeItem('branch')
    }
    window.location.reload()
  }

  return (
    <Select
      className={clsx(className, {'d-none': !loadingBranches && !branches.length})}
      value={branch}
      onChange={handleBranch}
      options={branches.map((item) => ({value: item.id, label: item.name}))}
      isLoading={loadingBranches}
    />
  )
}

export default BranchSwitcher
